import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import { UserReputation } from '@/types';

const TRUST_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; icon: typeof Shield }> = {
  Elite:       { label: 'Elite',       color: 'text-yellow-700 dark:text-yellow-300', bg: 'bg-yellow-50 dark:bg-yellow-900/20',  border: 'border-yellow-200', icon: ShieldCheck },
  Trusted:     { label: 'Trusted',     color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: ShieldCheck },
  Established: { label: 'Established', color: 'text-blue-700 dark:text-blue-300',   bg: 'bg-blue-50 dark:bg-blue-900/20',   border: 'border-blue-200',   icon: Shield },
  Growing:     { label: 'Growing',     color: 'text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200', icon: Shield },
  New:         { label: 'New',         color: 'text-gray-600 dark:text-zinc-400',   bg: 'bg-white dark:bg-zinc-900',   border: 'border-gray-200 dark:border-zinc-800',   icon: ShieldAlert },
};

interface Props {
  reputation: UserReputation;
  compact?: boolean;
  className?: string;
}

export default function ReputationBadge({ reputation, compact = false, className = '' }: Props) {
  const config = TRUST_CONFIG[reputation.trust_level] ?? TRUST_CONFIG.New;
  const Icon = config.icon;

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${config.bg} ${config.color} ${config.border} ${className}`}
      >
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  }

  return (
    <div className={`bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5 ${className}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs font-semibold text-gray-500 dark:text-zinc-500 uppercase tracking-wide mb-1">Reputation</p>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border ${config.bg} ${config.border}`}>
            <Icon className={`h-4 w-4 ${config.color}`} />
            <span className={`text-sm font-bold ${config.color}`}>{config.label} Trader</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{reputation.reputation_score}</p>
          <p className="text-xs text-gray-500 dark:text-zinc-500">reputation score</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-white dark:bg-zinc-900 rounded-xl p-2.5">
          <p className="text-lg font-bold text-gray-900 dark:text-zinc-100">{reputation.positive_reviews}</p>
          <p className="text-xs text-gray-500 dark:text-zinc-500">Positive</p>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-xl p-2.5">
          <p className="text-lg font-bold text-gray-900 dark:text-zinc-100">{reputation.successful_transactions}</p>
          <p className="text-xs text-gray-500 dark:text-zinc-500">Transactions</p>
        </div>
        <div className="bg-white dark:bg-zinc-900 rounded-xl p-2.5">
          <p className="text-lg font-bold text-gray-900 dark:text-zinc-100">
            {reputation.average_rating != null ? reputation.average_rating.toFixed(1) : '—'}
          </p>
          <p className="text-xs text-gray-500 dark:text-zinc-500">Avg Rating</p>
        </div>
      </div>

      {reputation.on_time_delivery_rate != null && (
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-zinc-500 border-t border-gray-50 pt-3">
          <span>On-time delivery</span>
          <span className="font-semibold text-gray-700 dark:text-zinc-300">{reputation.on_time_delivery_rate.toFixed(0)}%</span>
        </div>
      )}
    </div>
  );
}
